import { createContext, useContext, useState, useEffect } from 'react';

const GeoContext = createContext();

const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$', JPY: '¥', CNY: '¥',
  INR: '₹', BRL: 'R$', MXN: 'MX$', ZAR: 'R', NGN: '₦', KES: 'KSh',
  GHS: 'GH₵', EGP: 'E£', MAD: 'MAD', TZS: 'TSh', UGX: 'USh', XOF: 'CFA',
  XAF: 'FCFA', KRW: '₩', THB: '฿', PHP: '₱', IDR: 'Rp', MYR: 'RM',
  SGD: 'S$', HKD: 'HK$', TWD: 'NT$', NZD: 'NZ$', SEK: 'kr', NOK: 'kr',
  DKK: 'kr', CHF: 'CHF', PLN: 'zł', CZK: 'Kč', HUF: 'Ft', RON: 'lei',
  TRY: '₺', RUB: '₽', UAH: '₴', ILS: '₪', AED: 'AED', SAR: 'SAR',
  QAR: 'QAR', KWD: 'KD', BHD: 'BD', OMR: 'OMR', JMD: 'J$', TTD: 'TT$',
  BBD: 'Bds$', BSD: 'B$', BZD: 'BZ$', GYD: 'G$', SRD: 'SRD', HTG: 'G',
  DOP: 'RD$', CRC: '₡', PAB: 'B/.', COP: 'COL$', PEN: 'S/.', CLP: 'CL$',
  ARS: 'AR$', UYU: '$U', PYG: '₲', BOB: 'Bs', VES: 'Bs.S', PKR: '₨',
  BDT: '৳', LKR: 'Rs', NPR: 'Rs', MMK: 'K', LAK: '₭', KHR: '៛', VND: '₫',
};

// Country to currency mapping for common countries
const COUNTRY_CURRENCY = {
  US: 'USD', GB: 'GBP', UK: 'GBP', CA: 'CAD', AU: 'AUD', NZ: 'NZD',
  DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR', BE: 'EUR', AT: 'EUR', PT: 'EUR', IE: 'EUR', FI: 'EUR', GR: 'EUR',
  SE: 'SEK', NO: 'NOK', DK: 'DKK', CH: 'CHF', PL: 'PLN', CZ: 'CZK', HU: 'HUF', RO: 'RON',
  JP: 'JPY', CN: 'CNY', KR: 'KRW', IN: 'INR', PK: 'PKR', BD: 'BDT', LK: 'LKR',
  TH: 'THB', PH: 'PHP', ID: 'IDR', MY: 'MYR', SG: 'SGD', HK: 'HKD', TW: 'TWD', VN: 'VND',
  BR: 'BRL', MX: 'MXN', AR: 'ARS', CO: 'COP', PE: 'PEN', CL: 'CLP',
  ZA: 'ZAR', NG: 'NGN', KE: 'KES', GH: 'GHS', EG: 'EGP', MA: 'MAD', TZ: 'TZS', UG: 'UGX',
  TR: 'TRY', RU: 'RUB', UA: 'UAH', IL: 'ILS',
  AE: 'AED', SA: 'SAR', QA: 'QAR', KW: 'KWD', BH: 'BHD', OM: 'OMR',
  JM: 'JMD', TT: 'TTD', BB: 'BBD', BS: 'BSD', BZ: 'BZD', GY: 'GYD',
  DO: 'DOP', CR: 'CRC', PA: 'PAB', HT: 'HTG',
};

export function GeoProvider({ children }) {
  const [geo, setGeo] = useState({
    country: '',
    countryCode: '',
    region: '',
    city: '',
    currency: 'USD',
    currencySymbol: '$',
    rate: 1,
    loaded: false,
  });

  useEffect(() => {
    const fetchGeo = async () => {
      let country = '';
      let countryCode = '';
      let region = '';
      let city = '';
      let currency = 'USD';

      // Try multiple geo APIs with fallback
      const geoApis = [
        {
          url: 'https://free.freeipapi.com/api/json/',
          parse: (d) => ({ country: d.countryName, countryCode: d.countryCode, region: d.regionName, city: d.cityName, currency: d.currency?.code || d.currency || '' }),
        },
        {
          url: 'https://ipwho.is/',
          parse: (d) => ({ country: d.country, countryCode: d.country_code, region: d.region, city: d.city, currency: d.currency?.code || '' }),
        },
        {
          url: 'https://api.country.is/',
          parse: (d) => ({ country: '', countryCode: d.country || '', region: '', city: '', currency: '' }),
        },
      ];

      for (const api of geoApis) {
        try {
          const res = await fetch(api.url, { signal: AbortSignal.timeout(5000) });
          if (!res.ok) continue;
          const data = await res.json();
          const parsed = api.parse(data);
          if (parsed.countryCode) {
            countryCode = parsed.countryCode;
            country = parsed.country || countryCode;
            region = parsed.region || '';
            city = parsed.city || '';
            currency = parsed.currency || COUNTRY_CURRENCY[countryCode] || 'USD';
            break;
          }
        } catch (_) {
          continue;
        }
      }

      if (!currency || currency === 'USD') {
        // Use country code to determine currency if API didn't provide it
        currency = COUNTRY_CURRENCY[countryCode] || 'USD';
      }

      if (currency === 'USD') {
        setGeo({ country, countryCode, region, city, currency: 'USD', currencySymbol: '$', rate: 1, loaded: true });
        return;
      }

      // Get exchange rate
      try {
        const rateRes = await fetch('https://open.er-api.com/v6/latest/USD', { signal: AbortSignal.timeout(5000) });
        const rateData = await rateRes.json();
        const rate = rateData.rates?.[currency] || 1;

        setGeo({
          country, countryCode, region, city, currency,
          currencySymbol: CURRENCY_SYMBOLS[currency] || currency + ' ',
          rate, loaded: true,
        });
      } catch (_) {
        setGeo({ country, countryCode, region, city, currency, currencySymbol: CURRENCY_SYMBOLS[currency] || '$', rate: 1, loaded: true });
      }
    };

    fetchGeo();
  }, []);

  const convertPrice = (usdPrice) => usdPrice * geo.rate;

  const formatPrice = (usdPrice) => {
    const converted = convertPrice(usdPrice);
    if (geo.currency === 'USD') return `$${converted.toFixed(2)}`;
    return `${geo.currencySymbol}${converted.toFixed(2)}`;
  };

  const formatPriceDual = (usdPrice) => {
    if (geo.currency === 'USD') return `$${usdPrice.toFixed(2)}`;
    const converted = convertPrice(usdPrice);
    return `${geo.currencySymbol}${converted.toFixed(2)}`;
  };

  return (
    <GeoContext.Provider value={{ ...geo, convertPrice, formatPrice, formatPriceDual }}>
      {children}
    </GeoContext.Provider>
  );
}

export const useGeo = () => useContext(GeoContext);
